import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import get from 'lodash.get';

import AlbumFolderPurchaseSummary from '../../../components/purchase/album-folder-purchase/AlbumFolderPurchaseSummary';
import {
  resetAlbumDiscovery,
  onSelectIdentity,
  onDeselectIdentity,
  checkoutAlbumFolder,
  setJoinTypeSelectionFlag,
} from '../../../actions/discover-album-folder';
import { checkout } from '../../../graphql/mutations/checkout';
import {
  MeQuery,
  MeWithIdentities,
  MeWithIdentitiesWithTags,
  UserAnalyticsFunnelData,
} from '../../../graphql/queries/account';
import { AlbumFolderWithCreatorDetails } from '../../../graphql/queries/album-folder';
import { getAlbumFolderCheckoutItems } from '../../../util/discover-album-folder';
import { checkoutPrice } from '../../../graphql/queries/checkout';
import { openModal, requestConfirm } from '../../../actions/modal-actions';
import { joinByCode } from '../../../graphql/mutations/albums';
import { albumFolderJoin as albumFolderJoinMutation } from '../../../graphql/mutations/album-folder';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../apps/temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import {
  SEARCH_SUBSCRIPTION_PLAN,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
} from '../../../apps/temp/views/onboarding/PhotoSearchCheckout/helpers';
import {
  checkoutCartRequiresAddress,
  getCheckoutCartCheckoutItems,
} from '../../../services/checkout/redux/checkoutSelectors';
import { isPrepaidOnlyAlbumFolder } from '../../../util/album-folder';
import {
  clearCart,
  getIdentityCartItems,
  onIdentityCartItemsRemove,
  setActiveIdentity,
} from '../../../services/checkout/redux/checkoutActions';

const withUserData = graphql(MeQuery, {
  props: ({ data: { me, refetch } = {} }) => ({
    user: me,
    userAddress: me?.addresses?.[0],
    refetchAccount: refetch,
  }),
});

const withSubscribedItems = graphql(MeWithIdentitiesWithTags, {
  options: ({ match }) => ({
    variables: {
      albumFolderId: match.params.albumFolderId,
      albumFolderIds: [match.params.albumFolderId],
    },
  }),
  props: ({ data: { me, loading } }) => ({
    loadingSubscribedItems: loading,
    subscribedAlbumFolderIdentities: get(
      me,
      'subscribed_album_folder_identities.records',
      [],
    ),
  }),
});

// TODO: Investigate & Come up with proper solution.
// You might be wondering why do this. After a successful purchase
// has been completed (specifically on a new identity), queries should be
// re-fetched thanks to the checkout mutation, however, re-fetching does
// not trigger `loading` again, and worse than that, it happens async (with no await)
// which results in the RequirePurchasedAlbumFolder component to not have updated data
// meaning that *no identities have been purchased* and it redirects to the purchase
// page once again.
const withRefetchMeWithIdentities = graphql(MeWithIdentities, {
  props: ({ data: { refetch } }) => ({
    refetchMeWithIdentities: refetch,
  }),
});

const withAlbumFolderData = graphql(AlbumFolderWithCreatorDetails, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loadingAlbumFolder: loading,
  }),
});

const withCheckout = graphql(checkout, {
  props: ({ mutate, ownProps }) => ({
    checkout: ({
      accountId,
      accountAddressId,
      email,
      stripeToken,
      ppaItems,
      photoItems,
      customPrintItems,
      photoDownloadItems,
      searchSubscriptionItems,
      promoCode,
      stripePaymentMethodId,
      stripeConnectAccountId,
      digitalBundleItems,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            account_address_id: accountAddressId,
            email_address: email,
            stripe_token: stripeToken,
            photo_items: photoItems,
            subscription_items: ppaItems,
            custom_print_items: customPrintItems,
            photo_download_items: photoDownloadItems,
            search_subscription_items: searchSubscriptionItems,
            promo_code: promoCode,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
            digital_bundle_items: digitalBundleItems,
          },
        },
        refetchQueries: [
          {
            query: MeQuery,
          },
          {
            query: MeWithIdentities,
          },
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withJoinByCode = graphql(joinByCode, {
  props: ({ mutate, ownProps }) => ({
    joinByCode: (accountId, joinCode, identities, albumFolderJoin) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            join_code: joinCode,
            identities,
            album_folder_join: albumFolderJoin,
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withAlbumFolderJoin = graphql(albumFolderJoinMutation, {
  props: ({ mutate, ownProps }) => ({
    albumFolderJoin: ({ albumFolderId, identities }) =>
      mutate({
        variables: {
          input: {
            album_folder_id: albumFolderId,
            identity_ids: identities,
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const mapStateToProps = state => {
  const { discoverAlbumFolder } = state;
  return {
    // Filter out any identity that might not have an album folder selected
    // for purchase
    selectedIdentities: discoverAlbumFolder.identities.filter(
      identity => get(identity, 'albumFolders.length', 0) > 0,
    ),
    loading: discoverAlbumFolder.loading,
    error: discoverAlbumFolder.error,
    success: discoverAlbumFolder.success,
    purchasedUrl: discoverAlbumFolder.purchasedUrl,
    isPlicCheckout: discoverAlbumFolder.isPlicCheckout,
    checkoutItems: getAlbumFolderCheckoutItems(discoverAlbumFolder.identities),
    prepaidCheckoutItems: getCheckoutCartCheckoutItems(state),
    prepaidRequiresAddress: checkoutCartRequiresAddress(state),
  };
};

const mapDispatchToProps = {
  openModal,
  resetAlbumDiscovery,
  onDeselectIdentity,
  onIdentityCartItemsRemove,
  getIdentityCartItems,
  onSelectIdentity,
  setActiveIdentity,
  requestConfirm,
  checkoutAlbumFolder,
  setJoinTypeSelectionFlag,
  clearCart,
};

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({ checkoutItems, prepaidCheckoutItems, user, albumFolder }) => {
    const checkoutItemsToUse = isPrepaidOnlyAlbumFolder(albumFolder)
      ? prepaidCheckoutItems
      : {
          subscription_items: checkoutItems.ppaItems,
          custom_print_items: checkoutItems.customPrintItems,
        };

    return {
      variables: {
        account_id: user?.id,
        ...checkoutItemsToUse,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    };
  },
  props: ({ data: { checkoutPrice: price, loading, error } = {} }) => ({
    loadingFinalPrice: loading,
    finalPrice: price,
    checkoutPriceError: error,
  }),
  skip: ({ user, checkoutItems }) =>
    !user || !checkoutItems || !checkoutItems.ppaItems,
});

const withWaldoPlusMonthlySub = graphql(
  getPhotoSearchCheckoutSearchItemsQuery,
  {
    props: ({ data: { searchSubscriptionItems = [] } = {} }) => ({
      waldoPlusMonthlySubscription: searchSubscriptionItems.find(
        subscription =>
          subscription.plan === SEARCH_SUBSCRIPTION_PLAN.SHUTTERBUG_PLUS &&
          subscription.billing_period ===
            SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY,
      ),
    }),
  },
);

export default compose(
  withRouter,
  withUserData,
  withAlbumFolderData,
  connect(mapStateToProps, mapDispatchToProps),
  withSubscribedItems,
  withCheckoutPrice,
  withRefetchMeWithIdentities,
  withCheckout,
  withJoinByCode,
  withAlbumFolderJoin,
  withWaldoPlusMonthlySub,
)(AlbumFolderPurchaseSummary);
